/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {EmGroup} from "../../models/em-group";
import {GroupType} from "../../models/group-type";
import {InstanceData} from "../../models/instance-data";
import {LocationInstance} from "../../models/location-instance";
import {Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, Output, EventEmitter} from '@angular/core';
import {ActivatedRoute} from "@angular/router"

import {Reader} from "../../models/reader";
import {ReaderInstance} from "../../models/reader-instance";
import {GroupService} from "../../services/group.service";
import {LocationService} from "../../services/location.service";
import {ReaderService} from "../../services/reader.service";

import {AddToGroupDialogComponent} from "../add-to-group-dialog.component";
import {ConfirmDeleteDialogComponent} from "../confirm-delete-dialog.component";
import {ManageGroupsDialogComponent} from "../manage-groups-dialog.component";
import {RemoveFromGroupDialogComponent} from "../remove-from-group-dialog.component";
import {ShowGroupDialogComponent} from "../show-group-dialog.component";

import {EntityInstanceDialogComponent} from './dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./dialogs/entity-type-dialog.component";
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE} from "../../app-constants";
import { PagedResults } from "app/models/paged-results";
import { NgxSpinnerService } from "ngx-spinner";
import { FunctionParameterDataUi, GeneralFunctionDialogComponent } from "./dialogs/general-function-dialog.component";
import { SimpleIndefiniteProgressDialogComponent } from "../simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";

/** Filter Option representing the "REMOVE FILTER" Option */
const FilterOption_RemoveFilter = { column: null, label: "REMOVE FILTER" } as FilterOption;

@Component({
  templateUrl: './readers.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})

export class ReadersComponent implements OnInit {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  @ViewChild('fileUpload')
  mFileUploadInput: ElementRef;

  /* 
  * Going further if we want to display the status of function execution
  */
  // @ViewChild('functionExecutingDialog')
  // mFunctionExecutingDialog: FunctionExecutingDialogComponent;

  @ViewChild('generalDialog')
  mGeneralDialog: GeneralFunctionDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mReaders: ReaderUI[] = [];
  mReaderInstances: ReaderInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Readers, 2 = Reader Instances

  //Entity Type Dialog Variables
  mEntity: Reader;
  mEntityName: string;
  mEntityType = GroupType.READER;
  mEntityTypeName = "Reader";

  /** Filter Options available to the Encoding Scheme Types */
  filterOptionsForTypes: FilterOption[] = [];
  /** Selected Filter Option for the Encoding Scheme Types */
  filterOptionForTypes: FilterOption = FilterOption_RemoveFilter;
  /** Current Value for the Filter Option being used for the Encoding Scheme Types */
  filterValueForTypes: string = "";
  /** Filter options available to the Encoding Scheme Instances */
  filterOptionsForInstances: FilterOption[] = [];
  /** Selected Filter Option for the Encoding Scheme Instances */
  filterOptionForInstances: FilterOption = FilterOption_RemoveFilter;
  /** Current Value for the Filter Option being used for the Encoding Scheme Instances */
  filterValueForInstances: string = "";


  //Entity Instance Dialog Variables
  mInstance: ReaderInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: Reader;
  mInstanceType = GroupType.READERINSTANCE;
  mInstanceTypeName = "Reader";
  mAvailableEntityTypes: Reader[];
  mAvailableLocations: LocationInstance[];
  mSelectedLocation: LocationInstance;

  /** Total # of "Encoding Scheme" Types known to the system. (Used to figure out # of pages) */
  totalNumberOfTypes = 0;
  /** Total # of "Encoding Scheme" Instances known to the system. (Used to figure out he # of pages) */
  totalNumberOfInstances = 0;
  /** Current Page being displayed for the "Encoding Scheme" Types */
  currentPageForTypes = 1;
  /** Current Page being displayed for the "Encoding Scheme" Instances */
  currentPageForInstances = 1;

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];




  constructor(
    private mRoute: ActivatedRoute,
    private mReaderService: ReaderService,
    private mGroupService: GroupService,
    private mLocationService: LocationService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService,
  ) {}



  ngOnInit(): void {
    this.refreshFilterOptionsForTypes();
    this.refreshFilterOptionsForInstances();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];

    this.mRoute.data.subscribe(
      (data:
        {
          readers: PagedResults<Reader>,
          readerInstances: PagedResults<ReaderInstance>
        }
      ) => {
        this.updatePagedTypes(data.readers);
        this.updatePagedInstances(data.readerInstances);
      }
    )
  }
 
    onTypePageChange(pageNumber?: number):void {
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }
  
      if(this.currentPageForTypes == pageNumber) {
        return;
      }
  
      this.currentPageForTypes = pageNumber;
      this.refreshTypes();
    }
  
    private async refreshTypes(): Promise<void> {
      this.spinnerService.show();
  
      let pagedData: PagedResults<Reader>;
      try{
        pagedData = await this.mReaderService
          .getAllReadersForPaging((this.currentPageForTypes - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.filterValueForTypes);
      } catch (error) {
        this.spinnerService.hide();
        this.handleError(error);
        return;
      }
  
      this.updatePagedTypes(pagedData);
      this.spinnerService.hide();
  
    }
    
    private updatePagedTypes(pagedData: PagedResults<Reader>): void {
      const uiObjects: ReaderUI[] = [];
      if (pagedData.data != null) {
        pagedData.data.forEach(reader => {
          const ui = new ReaderUI();
          ui.mReader = reader;
          ui.mSelected = false;
          uiObjects.push(ui);
        });
      }
      this.mReaders = uiObjects;
      this.totalNumberOfTypes = pagedData.size;
    }
  
    onInstancePageChange(pageNumber ?: number): void {
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }
  
      if (this.currentPageForInstances == pageNumber) {
        return;
      }
  
      this.currentPageForInstances = pageNumber;
      this.refreshInstances();
  
    }
  
    private async refreshInstances(): Promise<void> {
      this.spinnerService.show();
  
      let pagedData: PagedResults<ReaderInstance> ;
      try {
        pagedData = await this.mReaderService.getAllReaderInstancesForPaging((this.currentPageForInstances - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.filterValueForInstances);
      } catch (error) {
        this.spinnerService.hide();
        this.handleError(error);
        return;
      }
  
      this.updatePagedInstances(pagedData);
      this.spinnerService.hide();
    }
  
    private updatePagedInstances(pagedData: PagedResults<ReaderInstance>): void {
      const uiObjects: ReaderInstanceUI[] = [];
      if (pagedData.data != null) {
        pagedData.data.forEach(readerInstance => {
          const ui = new ReaderInstanceUI();
          ui.mReaderInstance = readerInstance;
          ui.mSelected = false;
          uiObjects.push(ui);
        })
      }
      this.mReaderInstances = uiObjects;
      this.totalNumberOfInstances = pagedData.size;
    }
  
    private refreshFilterOptionsForTypes(): void {
      // Remove any existing Filters...
      while (this.filterOptionsForTypes.length > 0) {
        this.filterOptionsForTypes.pop();
      }
  
      // Add the "REMOVE FILTER" Option
      this.filterOptionsForTypes.push(FilterOption_RemoveFilter);
      // Add the "Name" Option
      this.filterOptionsForTypes.push({ column: "name", label: "Name" } as FilterOption);
    }
  
    private refreshFilterOptionsForInstances(): void {
      // Remove any existing Filters...
      while (this.filterOptionsForInstances.length > 0) {
        this.filterOptionsForInstances.pop();
      }
  
      // Add the "REMOVE FILTER" Option
      this.filterOptionsForInstances.push(FilterOption_RemoveFilter);
      // Add the "Name" Option
      this.filterOptionsForInstances.push({ column: "name", label: "Name" } as FilterOption);
    }

    manageResultPerPageClickedForTypes(value: number): void {
      this.selectedPageSizeValueForTypes = value;
      this.currentPageForTypes = 1;
      this.refreshTypes();
    }
  
    manageResultPerPageClickedForInstances(value: number): void {
      this.selectedPageSizeValueForInstances = value;
      this.currentPageForInstances = 1;
      this.refreshInstances();
    }


  getSelectedReaders(): Reader[] {
    const readers: Reader[] = [];
    this.mReaders.filter(ui => ui.mSelected).forEach(ui => readers.push(ui.mReader));
    return readers;
  }

  getSelectedReaderIds(): number[] {
    const ids: number[] = [];
    this.mReaders.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mReader.id));
    return ids;
  }

  getSelectedReaderNames(): string[] {
    const names: string[] = [];
    this.mReaders.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mReader.name));
    return names;
  }



  getSelectedReaderInstances(): ReaderInstance[] {
    const readerInstances: ReaderInstance[] = [];
    this.mReaderInstances.filter(ui => ui.mSelected).forEach(ui => readerInstances.push(ui.mReaderInstance));
    return readerInstances;
  }

  getSelectedReaderInstanceIds(): number[] {
    const ids: number[] = [];
    this.mReaderInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mReaderInstance.id));
    return ids;
  }

  getSelectedReaderInstanceNames(): string[] {
    const names: string[] = [];
    this.mReaderInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mReaderInstance.name));
    return names;
  }





  isSingleReaderSelected(): boolean {
    if (this.mReaders != null && this.mReaders.length > 0) {
      let oneSelected = false;
      for (const reader of this.mReaders) {
        if (reader.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyReadersSelected(): boolean {
    return this.mReaders.some(reader => reader.mSelected);
  }

  isAllReadersSelected(): boolean {
    return this.mReaders !== null && this.mReaders.every(reader => reader.mSelected);
  }

  setAllReadersSelected(event: any): void {
    this.mReaders.forEach(reader => reader.mSelected = event.target.checked);
  }





  isSingleReaderInstanceSelected(): boolean {
    if (this.mReaderInstances != null && this.mReaderInstances.length > 0) {
      let oneSelected = false;
      for (const readerInstance of this.mReaderInstances) {
        if (readerInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyReaderInstancesSelected(): boolean {
    return this.mReaderInstances.some(readerInstance => readerInstance.mSelected);
  }

  isAllReaderInstancesSelected(): boolean {
    return this.mReaderInstances !== null && this.mReaderInstances.every(readerInstance => readerInstance.mSelected);
  }

  setAllReaderInstancesSelected(event: any): void {
    this.mReaderInstances.forEach(readerInstance => readerInstance.mSelected = event.target.checked);
  }





  manageReaderGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.READER);
  }

  manageReaderInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.READERINSTANCE);
  }

  addSelectedReadersToGroupClicked(): void {
    if (this.isAnyReadersSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.READER, this.getSelectedReaderNames());
    }
  }

  addSelectedReaderInstancesToGroupClicked(): void {
    if (this.isAnyReaderInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.READERINSTANCE, this.getSelectedReaderInstanceNames());
    }
  }

  removeSelectedReadersFromGroupClicked(): void {
    if (this.isAnyReadersSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const readerNames: string[] = [];
      this.getSelectedReaders().forEach(reader => {
        readerNames.push(reader.name);
        if (reader.groups != null) {
          reader.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, readerNames);
    }
  }

  removeSelectedReaderInstancesFromGroupClicked(): void {
    if (this.isAnyReaderInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const readerInstanceNames: string[] = [];
      this.getSelectedReaderInstances().forEach(readerInstance => {
        readerInstanceNames.push(readerInstance.name);
        if (readerInstance.groups != null) {
          readerInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, readerInstanceNames);
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.READER: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedReaderIds())
          .then(
          success => {
            this.refreshTypes;
          },
          error => this.handleError(error)
          );
        break;
      }
      case GroupType.READERINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedReaderInstanceIds())
          .then(
          success => {
            this.refreshInstances();
          },
          error => this.handleError(error)
          );
        break;
      }
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.READER: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedReaderIds())
          .then(
          success => {
            this.refreshTypes();
          },
          error => this.handleError(error)
          );
        break;
      }
      case GroupType.READERINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedReaderInstanceIds())
          .then(
          success => {
            this.refreshInstances();
          },
          error => this.handleError(error)
          );
        break;
      }
    }
  }





  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.READER: {
        this
          .mGroupService
          .getAllReadersInGroup(group.id)
          .then(
          result => {
            const itemNames: string[] = [];
            result.forEach(groupMember => itemNames.push(groupMember.name));
            this.mShowGroupDialog.show(group.name, itemNames);
          },
          error => this.handleError(error)
          );
        break;
      }
      case GroupType.READERINSTANCE: {
        this
          .mGroupService
          .getAllReaderInstancesInGroup(group.id)
          .then(
          result => {
            const itemNames: string[] = [];
            result.forEach(groupMember => itemNames.push(groupMember.name));
            this.mShowGroupDialog.show(group.name, itemNames);
          },
          error => this.handleError(error)
          );
        break;
      }
    }
  }





  addReaderClicked(): void {
    this.mEntity = new Reader();
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.entityParent = null;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedReaderClicked(): void {
    if (this.isSingleReaderSelected()) {
      this.editReader(this.getSelectedReaders()[0]);
    }
  }

  duplicateSelectedReaderClicked(): void {
    if (this.isSingleReaderSelected()) {
      this
        .mReaderService
        .getReaderById(this.getSelectedReaders()[0].id)
        .then(
        response => {
          response.id = null;
          if (response.properties != null) {
            response.properties.forEach(p => p.id = null);
          }
          if (response.functions != null) {
            response.functions.forEach(f => f.id = null);
          }

          this.mEntity = response;
          this.mEntityName = null;
          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
        );
    }
  }

  deleteSelectedReadersClicked(): void {
    if (this.isAnyReadersSelected()) {
      this.mDeleteFlag = 1; //1 = Readers
      this.mConfirmDeleteDialog.show("reader type", this.getSelectedReaderNames());
    }
  }

  editReader(reader: Reader): void {
    this
      .mReaderService
      .getReaderById(reader.id)
      .then(
      response => {
        this.mEntity = response;
        this.mEntityName = response.name;
        this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

        if (response.parent != null) {
          this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
        } else {
          this.mEntityTypeDialogComponent.entityParent = null;
        }

        this.mEntityTypeDialogComponent.showDialog();
      },
      error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Reader... Create it
      this
        .mReaderService
        .createReader(this.mEntity)
        .then(
        response => {
          this.refreshTypes();
          this.mEntityTypeDialogComponent.hideDialog();
        },
        error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Reader... Update it
      this
        .mReaderService
        .updateReader(this.mEntity)
        .then(
        response => {
          this.refreshTypes();
          this.mEntityTypeDialogComponent.hideDialog();
        },
        error => this.handleError(error)
        );
    }
  }





  addReaderInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new ReaderInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataRefreshLocationsAndShowDialog();
  }

  editSelectedReaderInstanceClicked(): void {
    if (this.isSingleReaderInstanceSelected()) {
      this.editReaderInstance(this.getSelectedReaderInstances()[0]);
    }
  }

  /**
   * Called when the "Reset Driver" List Item is clicked in the "OPTIONS" drop down.
   */
  resetDriverClicked(): void {
    if (this.isSingleReaderInstanceSelected()) {
      console.log(`Indefinite Progress Dialog: ${this.indefiniteProgressDialog}`)
      this.indefiniteProgressDialog.show("Performing Action", "Reseting Driver...");
      this._resetDriver();
    }
  }

  /**
   * Does the underlying logic for resetting a Reader's Driver
   */
  private async _resetDriver(): Promise<void> {
    const readerId = this.getSelectedReaderInstanceIds()[0];
    console.log(`Reseting Reader Driver for ID: ${readerId}`);

    try {
      await this.mReaderService.resetReaderDriver(readerId);
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.handleError(error);
      return;
    }

    this.refreshInstances();
    this.indefiniteProgressDialog.hide();
  }

  duplicateSelectedReaderInstanceClicked(): void {
    if (this.isSingleReaderInstanceSelected()) {
      this
        .mReaderService
        .getReaderInstanceById(this.getSelectedReaderInstances()[0].id)
        .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataRefreshLocationsAndShowDialog();
        },
        error => this.handleError(error)
        );
    }
  }

  deleteSelectedReaderInstancesClicked(): void {
    if (this.isAnyReaderInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Reader Instances
      this.mConfirmDeleteDialog.show("reader", this.getSelectedReaderInstanceNames());
    }
  }

  editReaderInstance(readerInstance: ReaderInstance): void {
    this
      .mReaderService
      .getReaderInstanceById(readerInstance.id)
      .then(
      response => {
        //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
        this.mInstance = response;

        //Set the Instance Name which is displayed in the Dialog Header
        this.mInstanceName = response.name;

        // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
        this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

        //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
        this.refreshTypesReloadDataRefreshLocationsAndShowDialog();
      },
      error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataRefreshLocationsAndShowDialog(): Promise<void> {
    // Need to grab ALL the readers, not just the ones on the page we're viewing
    this.mAvailableEntityTypes = await this.mReaderService.getAllReaders();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.reader == null ? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.reader.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.properties = this.mInstance.reader.properties;
      this.mInstanceEntityType.functions = this.mInstance.reader.functions;
    }

    //Refresh the available and selected Locations
    this.mSelectedLocation = null;
    this
      .mLocationService
      .getAllLocationInstances()
      .then(
        response => {
          //Set the possible Location values so that it can be used by the EntityInstanceDialogComponent
          this.mAvailableLocations = response;

          //Figure out if a Location was previously selected for the Reader, get the correct version and store it
          this.mSelectedLocation = this.mInstance == null || this.mInstance.location == null ? null : this.mAvailableLocations.find(location => location.id == this.mInstance.location.id);

          // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
          this.cdr.detectChanges();

          //Refresh the Instance Data, loading the existing values in for the Instance being edited
          this.mEntityInstanceDialogComponent.refreshData();

          //Show the Entity Instance Dialog
          this.mEntityInstanceDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityInstance(): void {
    //Set the values to the Reader Instance to be created
    this.mInstance.reader = this.mInstanceEntityType;
    this.mInstance.location = this.mSelectedLocation;
    //this.instanceData.forEach(d => {
    //  if (d.value != null && d.value.length > 0) {
    //    this.mInstance.data.push(d);
    //  }
    //});

    if (this.mInstance.id == null) {
      //ID is null, Reader Instance does not exist!  Create it...
      this
        .mReaderService
        .createReaderInstance(this.mInstance)
        .then(
        response => {
          this.refreshInstances();
          this.mEntityInstanceDialogComponent.hideDialog();
        },
        error => this.handleError(error)
        );
    } else {
      //ID is not null, Reader Instance exists!  Update it...
      this
        .mReaderService
        .updateReaderInstance(this.mInstance)
        .then(
        response => {
          this.refreshInstances();
          this.mEntityInstanceDialogComponent.hideDialog();
        },
        error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: Reader): void {
    console.log('selected Entity');
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null) {
      this
        .mReaderService
        .getReaderById(this.mInstanceEntityType.id)
        .then(
        foundEntityType => {
          this.mInstanceEntityType.properties = foundEntityType.properties;
          this.mInstanceEntityType.functions = foundEntityType.functions;
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  selectedLocationChanged(location: LocationInstance): void {
    this.mSelectedLocation = location;
  }

  refreshInstanceData(): void {
    this
      .mReaderService
      .getReaderInstanceById(this.mInstance.id)
      .then(
      response => {
        this.mInstance.data = response.data;
        this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
        this.mEntityInstanceDialogComponent.refreshData();
      },
      error => this.handleError(error)
      );
  }





  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Readers
      this
        .mReaderService
        .deleteReaders(this.getSelectedReaderIds())
        .then(
        success => {
          this.refreshTypes();
        },
        error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = Reader Instances
      this
        .mReaderService
        .deleteReaderInstances(this.getSelectedReaderInstanceIds())
        .then(
        success => {
          this.refreshInstances();
        },
        error => this.handleError(error)
        );
    }
  }


  uploadFileClicked(): void {
    this.mFileUploadInput.nativeElement.click();
  }

  onFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadReaders(event.target.files[0]);
    }
  }

  uploadReaders(file: any): void {
    const formData = new FormData();
    formData.append("file", file);

    this.mReaderService.uploadReaders(formData).then(
        response => {
          console.log('Success');
        },
        error => {
          console.error(error);
        }
    );
  }

  downloadReaders(): void {
    this.mReaderService.downloadReaders().then(
        response => {
           const blob = new Blob([response], { type: 'text/csv' });
           const url = window.URL.createObjectURL(blob);
           const a = document.createElement("a");

           document.body.appendChild(a);
//           a.style = 'diplay: none';
           a.href = url;
           a.download = 'objects.csv';
           a.click();
           window.URL.revokeObjectURL(url);
        },
        error => {
          console.error(error);
        }
    );
  }

 

  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

  /**-------------------------------------------------------------------------------------------------------------------- */

  onFilterOptionForTypesClicked(filterOption: FilterOption): void {
    if (this.filterOptionForTypes == filterOption) {
      return;
    }

    this.filterOptionForTypes = filterOption;
    this.filterValueForTypes = "";
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  onFilterValueForTypesChanged(): void {
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  onFilterOptionForInstancesClicked(filterOption: FilterOption): void {
    if (this.filterOptionForInstances == filterOption) {
      return;
    }

    this.filterOptionForInstances = filterOption;
    this.filterValueForInstances = "";
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }

  onFilterValueForInstancesChanged(): void {
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }

  /* 
    *  If the property for reader_status exists then display null.
    *  If the property exists and has the values mentioned then display the reader_status
    *  If the property is not present then just display an empty string
  */
  getReaderInstanceStatus(readerInstance: ReaderInstance): string {
    //this.refreshInstances();

    try {
      const readerInstanceStatus = readerInstance.data.find((data) => data.property.property === "reader_status");
      //console.log(readerInstanceStatus.value);
      return readerInstanceStatus.value = readerInstanceStatus.value == null ? "None" : readerInstanceStatus.value;
    }
    catch(error) {
      return "";
    }
  }

  /* 
  *  Refresh the Status for Readers Instances
  */
  refreshPageToUpdateStatus(): void {
    console.log("Refreshing Reader Instance Page");
    // Added a timeout here since the Status doesn't change immediately and requires some time to show up
    setTimeout(() => { this.refreshInstances() }, 2000); // Waiting for 3 seconds
      this.refreshInstances();
  }

  /*  
  *  Handling the Right click event only on Reader Instance Name Column
  */
  fetchFunctionList(event: MouseEvent, readerInstance: ReaderInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = [];
 
    // Send the request to get the Reader Instance details using the ID
    this
      .mReaderService
      .getReaderInstanceById(readerInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the dropdown to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.reader && Array.isArray(response.reader.functions) && (response.reader.functions).length > 0) {
                this.functionList = response.reader.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );
      
  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    // If its a Dummy do not open the General Dialog Box
    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }
    
    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }

  //  functionStartingToExecute(func: Function): void {
  //   this.mFunctionExecutingDialog.showDialogStartExecutingFunction(func.name);
  // }

  //  functionFinishedExecutingSuccessfully(): void {
  //   this.mFunctionExecutingDialog.showFunctionExecutionSuccess();
  // }

  //  functionFinishedExecutingWithError(error: string): void {
  //   this.mFunctionExecutingDialog.showFunctionExecutionError(error);
  //   this.showGeneralDialog = false;
  // }


}

class ReaderUI {
  mSelected = false;
  mReader: Reader;
}

class ReaderInstanceUI {
  mSelected = false;
  mReaderInstance: ReaderInstance;
}

class FilterOption {
  column: string;
  label: string;
}


